import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Link,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { buildPublicFileUrl } from '../../config/env';
import { EmptyState, FilterBar, FormSection, LoadingState } from '../../components/ui';
import { RequestReviewCard } from '../../components/manager/ManagerVisuals';
import { findOverlaps, getLeaveTypeLabel } from '../../utils/leaveVisuals';
import {
  EXECUTIVE_ROUTE_ROLES,
  HOD_ROUTE_ROLES,
  OPERATIONS_ROUTE_ROLES,
  normalizeRoleKey,
} from '../../utils/roleCapabilities';

const MANAGER_API_ROLES = new Set(['manager', ...HOD_ROUTE_ROLES, ...OPERATIONS_ROUTE_ROLES].map(normalizeRoleKey));
const CEO_API_ROLES = new Set(['ceo', ...EXECUTIVE_ROUTE_ROLES].map(normalizeRoleKey));

function resolveVacationApiBaseUrl() {
  const candidates = [localStorage.getItem('role'), localStorage.getItem('actual_role')]
    .map(normalizeRoleKey)
    .filter(Boolean);
  if (candidates.some((role) => CEO_API_ROLES.has(role))) return '/ceo';
  if (candidates.some((role) => role === 'finance')) return '/finance';
  if (candidates.some((role) => MANAGER_API_ROLES.has(role))) return '/manager';
  return '/manager';
}

function pickArray(payload) {
  if (Array.isArray(payload)) return payload;
  const candidates = ['data', 'rows', 'items', 'records', 'results', 'result', 'vacations', 'requests', 'list'];
  for (const key of candidates) {
    if (Array.isArray(payload?.[key])) return payload[key];
  }
  if (payload && typeof payload === 'object') {
    for (const key of Object.keys(payload)) {
      if (Array.isArray(payload[key])) return payload[key];
    }
  }
  return [];
}

function normalizeRows(input) {
  const arr = pickArray(input);
  return arr.map((row, index) => ({ id: row.id ?? row.request_id ?? row._id ?? index, ...row }));
}

function toStatusKey(status) {
  if (status == null) return 'pending';
  const s = String(status).trim().toLowerCase();
  if (s === '0' || s === 'pending' || s === 'awaiting' || s === 'in_review') return 'pending';
  if (s === '1' || s === 'approved' || s === 'accepted') return 'approved';
  if (s === '2' || s === 'rejected' || s === 'declined') return 'rejected';
  return s;
}

function getEmployeeName(row) {
  return row?.user?.name || row?.employee_name || 'Unknown';
}
function getLeaveType(row) {
  return row?.leave_type ?? row?.type ?? '-';
}
function getStartDate(row) {
  return row?.start_date ?? row?.start ?? '-';
}
function getEndDate(row) {
  return row?.end_date ?? row?.end ?? '-';
}

function FieldRow({ label, value }) {
  return (
    <Grid size={{ xs: 12, sm: 6 }}>
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
        <strong>{label}: </strong>
        {value ?? 'N/A'}
      </Typography>
    </Grid>
  );
}

export default function ManagerRequestsList() {
  const theme = useTheme();
  const baseUrl = resolveVacationApiBaseUrl();

  const [rows, setRows] = useState([]);
  const [referenceRows, setReferenceRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [queueMode, setQueueMode] = useState('pending');
  const [search, setSearch] = useState('');

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [action, setAction] = useState(null);
  const [activeRow, setActiveRow] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [viewOpen, setViewOpen] = useState(false);
  const [viewRow, setViewRow] = useState(null);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((row) => {
      const haystack = `${getEmployeeName(row)} ${getLeaveType(row)} ${row?.status || ''}`.toLowerCase();
      return haystack.includes(term);
    });
  }, [rows, search]);

  const stats = useMemo(() => {
    const pending = rows.filter((row) => toStatusKey(row.status) === 'pending').length;
    const approved = rows.filter((row) => toStatusKey(row.status) === 'approved').length;
    const rejected = rows.filter((row) => toStatusKey(row.status) === 'rejected').length;
    return [
      { label: 'Pending action', value: pending, caption: 'Awaiting your decision', color: theme.palette.warning.main },
      { label: 'Approved', value: approved, caption: 'In this view', color: theme.palette.success.main },
      { label: 'Rejected', value: rejected, caption: 'In this view', color: theme.palette.error.main },
      { label: 'Visible', value: filteredRows.length, caption: queueMode === 'pending' ? 'Pending view' : 'Archive view', color: theme.palette.primary.main },
    ];
  }, [rows, filteredRows.length, queueMode, theme.palette]);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [listResponse, referenceResponse] = await Promise.all([
        api.get(`${baseUrl}/vacation-requests`, { params: { view_mode: 'assigned_requests', queue: queueMode, paginate: false } }),
        api.get(`${baseUrl}/vacation-requests`, { params: { paginate: false } }).catch(() => ({ data: [] })),
      ]);
      setRows(normalizeRows(listResponse.data));
      setReferenceRows(normalizeRows(referenceResponse.data));
    } catch (err) {
      console.error(err);
      setRows([]);
      setError(err?.response?.data?.message || 'Could not load team vacation requests.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queueMode]);

  const openConfirm = (row, nextAction) => {
    setActiveRow(row);
    setAction(nextAction);
    setConfirmOpen(true);
  };

  const closeConfirm = () => {
    if (submitting) return;
    setConfirmOpen(false);
    setActiveRow(null);
    setAction(null);
  };

  const doAction = async () => {
    if (!activeRow?.id || !action) return;
    setSubmitting(true);
    try {
      await api.patch(`${baseUrl}/vacation-requests/${activeRow.id}`, { status: action });
      toast.success(`Request ${action === 'Approved' ? 'approved' : 'declined'}.`);
      await load();
      if (typeof window.refreshAssignedPendingCount === 'function') window.refreshAssignedPendingCount();
      if (typeof window.refreshAssignedPendingVacationCount === 'function') window.refreshAssignedPendingVacationCount();
      setConfirmOpen(false);
      setActiveRow(null);
      setAction(null);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || 'Could not update request.');
    } finally {
      setSubmitting(false);
    }
  };

  const openView = (row) => {
    setViewRow(row);
    setViewOpen(true);
  };

  return (
    <Box sx={{ mt: 1 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' },
          gap: 0.85,
          mb: 1.15,
        }}
      >
        {stats.map((item) => (
          <Paper
            key={item.label}
            variant="outlined"
            sx={{ height: '100%', p: 1, borderRadius: '12px', borderColor: alpha(item.color, 0.2), bgcolor: alpha(item.color, theme.palette.mode === 'dark' ? 0.12 : 0.06) }}
          >
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                  {item.label}
                </Typography>
                <Typography variant="h5" sx={{ mt: 0.1, fontWeight: 900, letterSpacing: -0.6, lineHeight: 1 }}>
                  {item.value}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {item.caption}
                </Typography>
              </Box>
              <Box sx={{ width: 8, height: 36, borderRadius: 999, bgcolor: alpha(item.color, 0.72), flexShrink: 0 }} />
            </Stack>
          </Paper>
        ))}
      </Box>

      <FilterBar
        sx={{
          mb: 1.15,
          '& .MuiTextField-root': { minWidth: { xs: '100%', sm: 280 } },
          '& .MuiToggleButton-root': { borderRadius: '8px !important', textTransform: 'none', fontWeight: 800 },
        }}
        left={
          <>
            <TextField
              size="small"
              placeholder="Search employee, type, status"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              InputProps={{ startAdornment: <SearchRoundedIcon fontSize="small" sx={{ mr: 0.8, opacity: 0.65 }} /> }}
            />
            <ToggleButtonGroup size="small" exclusive value={queueMode} onChange={(_, value) => value && setQueueMode(value)}>
              <ToggleButton value="pending">Pending action</ToggleButton>
              <ToggleButton value="archive">Archive</ToggleButton>
            </ToggleButtonGroup>
          </>
        }
        right={
          <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
            Refresh
          </Button>
        }
      />

      {loading ? (
        <LoadingState rows={5} />
      ) : error ? (
        <Alert
          severity="error"
          sx={{ borderRadius: '12px' }}
          action={
            <Button color="inherit" size="small" onClick={load}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      ) : filteredRows.length === 0 ? (
        <EmptyState
          compact
          title="No vacation requests found"
          description={queueMode === 'pending' ? 'Nothing is waiting on you right now.' : 'No archived requests match this view.'}
        />
      ) : (
        <Stack spacing={0.9}>
          {filteredRows.map((row) => (
            <RequestReviewCard
              key={row.id}
              request={row}
              overlaps={findOverlaps(row, referenceRows)}
              busy={submitting && activeRow?.id === row.id}
              onApprove={(target) => openConfirm(target, 'Approved')}
              onReject={(target) => openConfirm(target, 'Rejected')}
              onView={openView}
            />
          ))}
        </Stack>
      )}

      <Dialog open={viewOpen} onClose={() => setViewOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 900 }}>
            Leave request overview
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Review the employee, dates, reason, notes, and attachment before taking action.
          </Typography>
        </DialogTitle>
        {viewRow ? (
          <DialogContent dividers>
            <FormSection title="Request details" description="Vacation request summary" tone="default">
              <Grid container spacing={1.6}>
                <FieldRow label="Employee" value={getEmployeeName(viewRow)} />
                <FieldRow label="Leave type" value={getLeaveTypeLabel(getLeaveType(viewRow))} />
                <FieldRow label="Start date" value={getStartDate(viewRow)} />
                <FieldRow label="End date" value={getEndDate(viewRow)} />
                <FieldRow label="Status" value={String(viewRow.status ?? 'Pending')} />
                <FieldRow label="Request ID" value={viewRow.id ?? viewRow.request_id} />
                {viewRow.duration_in_days || viewRow.days || viewRow.duration ? (
                  <FieldRow label="Duration" value={viewRow.duration_in_days ?? viewRow.days ?? viewRow.duration} />
                ) : null}
                {viewRow.created_at ? <FieldRow label="Submitted" value={viewRow.created_at} /> : null}
                {viewRow.reason ? <FieldRow label="Reason" value={viewRow.reason} /> : null}
                {viewRow.notes ? <FieldRow label="Notes" value={viewRow.notes} /> : null}
                {viewRow.attachment ? (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2">
                      <strong>Attachment: </strong>
                      <Link href={buildPublicFileUrl(viewRow.attachment)} target="_blank" rel="noopener noreferrer" underline="hover">
                        View file
                      </Link>
                    </Typography>
                  </Grid>
                ) : null}
              </Grid>
            </FormSection>
          </DialogContent>
        ) : null}
        <DialogActions>
          {viewRow && toStatusKey(viewRow.status) === 'pending' ? (
            <>
              <Button
                color="error"
                onClick={() => {
                  setViewOpen(false);
                  openConfirm(viewRow, 'Rejected');
                }}
              >
                Decline
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={() => {
                  setViewOpen(false);
                  openConfirm(viewRow, 'Approved');
                }}
              >
                Approve
              </Button>
            </>
          ) : null}
          <Button onClick={() => setViewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmOpen} onClose={closeConfirm} PaperProps={{ sx: { borderRadius: '12px' } }}>
        <DialogTitle>Confirm action</DialogTitle>
        <DialogContent dividers>
          <Typography>
            Are you sure you want to <strong>{action === 'Approved' ? 'approve' : 'decline'}</strong>{' '}
            {activeRow ? `${getEmployeeName(activeRow)}'s ${getLeaveTypeLabel(getLeaveType(activeRow))} request` : 'this request'}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirm} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="contained" color={action === 'Approved' ? 'success' : 'error'} onClick={doAction} disabled={submitting}>
            {submitting ? 'Saving…' : `Yes, ${action === 'Approved' ? 'approve' : 'decline'}`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
